<template>
  <div>
    <h1>Print document(s)</h1>
    <FileWorking :file="file" />
    <v-alert :value="true" type="info"
      >Choose the number of copies you want for each document below. Then click
      the print button at the bottom. The number of copies has already been set
      from the template defaults. Click the link above to return to the file
      without printing.</v-alert
    >
    <h2>Documents</h2>
    <v-btn @click="clearCopies()">Clear copies</v-btn>
    <v-btn @click="clearCopiesFile()">Clear file copies</v-btn>
    <table border="1">
      <tr>
        <th>Document</th>
        <th>No. copies</th>
        <th>No. file copies</th>
      </tr>
      <tr v-for="document in modifiedDocuments" :key="document.id">
        <td>
          <span v-if="document.template"
            >{{ document.template.code }} - {{ document.template.name }}</span
          >
          <span v-if="!document.template">{{ document.id }}</span>
        </td>
        <td>
          <v-select
            v-if="printDocuments && printDocuments[document.id]"
            v-model="printDocuments[document.id].copies"
            :items="printValues"
            item-value="id"
            item-text="body"
          />
        </td>
        <td>
          <v-select
            v-if="printDocuments && printDocuments[document.id]"
            v-model="printDocuments[document.id].copiesfile"
            :items="printValues"
            item-value="id"
            item-text="body"
          />
        </td>
      </tr>
    </table>
    <h2>Common uploads</h2>
    <v-alert :value="true" type="warning"
      >The system cannot currently print uploads</v-alert
    >
    <table border="1">
      <tr>
        <th>Upload</th>
        <th>No. copies</th>
      </tr>
      <tr v-for="uploadFile in uploadFiles" :key="uploadFile.id">
        <td>
          <span v-if="uploadFile.uploadDescription">{{
            uploadFile.uploadDescription.body
          }}</span>
          <span v-if="!uploadFile.uploadDescription">{{ uploadFile.id }}</span>
        </td>
        <td>
          <v-select
            v-model="printUploadFiles[uploadFile.id].copies"
            :items="printValuesEmpty"
            item-value="id"
            item-text="body"
          />
        </td>
      </tr>
    </table>
    <v-btn
      @click="print"
      :disabled="printingDocuments"
      :loading="printingDocuments"
    >
      <v-icon>print</v-icon>&#160;Print documents and return to the file
    </v-btn>
    <v-dialog v-model="printingDocuments" persistent width="300">
      <v-card color="error" dark>
        <v-card-text>
          <span>Waiting for server to print documents...</span>
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FileWorking from "@/components/link/FileWorking";
import nbmlaw from "@/api/nbmlaw";
export default {
  name: "DocumentPrint",
  metaInfo() {
    return {
      title: "Document print",
      titleTemplate: "%s - Document - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  components: {
    FileWorking
  },
  computed: {
    modifiedDocuments() {
      if (!this.documents) {
        return undefined;
      }
      return this.documents.map((d) => {
        const template =
          this.templates && this.templates[d.id]
            ? this.templates[d.id]
            : undefined;
        return { ...d, template };
      });
    }
  },
  data() {
    return {
      file: null,
      documents: null,
      templates: null,
      uploadFiles: null,
      printValues: [
        { id: 0, body: "0" },
        { id: 1, body: "1" },
        { id: 2, body: "2" },
        { id: 3, body: "3" },
        { id: 4, body: "4" },
        { id: 5, body: "5" },
        { id: 6, body: "6" }
      ],
      printValuesEmpty: [{ id: 0, body: "0" }],
      printDocuments: null,
      printUploadFiles: null,
      printingDocuments: false
    };
  },
  methods: {
    async fetchFile() {
      const response = await nbmlaw.get(
        `/cors/admin/file/files/${this.$route.params.objectId}`
      );
      // console.log(response.data);
      this.file = response.data;
    },
    async fetchDocuments() {
      if (this.$route.query.documents && this.$route.query.documents.length) {
        const response = await nbmlaw.get(
          `/cors/admin/document/documents/multiple/${this.$route.query.documents}`
        );
        // console.log(response.data);
        this.documents = response.data;
      }
    },
    async fetchTemplatesByDocument() {
      if (this.$route.query.documents && this.$route.query.documents.length) {
        const response = await nbmlaw.get(
          `/cors/admin/document/documents/multiple/${this.$route.query.documents}/templates`
        );
        // console.log(response.data);
        this.templates = response.data;
      }
    },
    async fetchUploadFiles() {
      const response = await nbmlaw.get(
        `/cors/admin/document/uploadFiles/multiple/${this.$route.query.uploads}`
      );
      // console.log(response.data);
      this.uploadFiles = response.data;
    },
    clearCopies() {
      if (this.printDocuments) {
        for (var prop in this.printDocuments) {
          this.printDocuments[prop].copies = 0;
        }
      }
    },
    clearCopiesFile() {
      if (this.printDocuments) {
        for (var prop in this.printDocuments) {
          this.printDocuments[prop].copiesFile = 0;
        }
      }
    },
    async print() {
      this.printingDocuments = true;
      try {
        await nbmlaw.post(
          `/cors/admin/document/documents/print`,
          this.printDocuments
        );
        // console.log(response.data);
        this.$router.push({
          name: "file",
          params: {
            id: this.$route.params.objectId
          }
        });
      } catch (err) {
        this.printingDocuments = false;
      }
    }
  },
  watch: {
    modifiedDocuments: {
      handler() {
        if (this.modifiedDocuments && this.modifiedDocuments.length) {
          this.printDocuments = this.modifiedDocuments.reduce(function (
            map,
            obj
          ) {
            map[obj.id] = {
              copies: obj.template.ncopies,
              copiesfile: obj.template.ncopiesfile
            };
            return map;
          },
          {});
        }
      }
    },
    uploadFiles: {
      handler() {
        if (this.uploadFiles && this.uploadFiles.length) {
          this.printUploadFiles = this.uploadFiles.reduce(function (map, obj) {
            map[obj["id"]] = map[obj["id"]] || [];
            map[obj["id"]] = { copies: 0 };
            return map;
          }, {});
        }
      }
    }
  },
  created() {
    this.fetchFile();
    this.fetchDocuments();
    this.fetchTemplatesByDocument();
    if (this.$route.query.uploads && this.$route.query.uploads.length) {
      this.fetchUploadFiles();
    }
  }
};
</script>
